import { detectOverflow } from '@popperjs/core';
import { noop } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type Modifier, usePopper } from 'react-popper';
import { useTheme } from 'styled-components';
import { useCurrentDocument } from '../../hooks/useCurrentDocument';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useOverridePopoverProps } from '../../providers/OverridePopoverPropsProvider';
import { EMPTY_ARRAY } from '../../utils/empty';
import { ensurePointerPositionTracking, isPointerOverElement, recordPointerPosition } from './pointerPosition';
import { POPOVER_PORTAL_ID } from './tokens';
import type { PopoverState, PopoverStateProps } from './types';

function getPortaledAvailableHeight(
  placement: string,
  overflow: { top: number; bottom: number },
  referenceHeight: number
): number | undefined {
  if (placement.startsWith('top') || placement.startsWith('bottom')) {
    // Larger gap so a top-start tooltip near the top of the screen cannot shrink enough that flip never runs.
    return Math.max(0, -overflow.top, -overflow.bottom);
  }
  if (placement.startsWith('left') || placement.startsWith('right')) {
    // Side menus slide vertically; max height is the padded clipping rect, not the gap above/below the target.
    return Math.max(0, -overflow.top + referenceHeight - overflow.bottom);
  }
  return undefined;
}

export function usePopoverState({
  closeOnClickOutside = false,
  onClickOutside = noop,
  trigger = 'click',
  isSmall = false,
  usePortal = false,
  onOpen = noop,
  onClose = noop,
  placement,
  modifiers = EMPTY_ARRAY,
  delay,
  hideDelay,
  strategy = 'absolute',
  preventOverflow = true,
  noPaddingAndBorder = false,
  popoverDocument,
  resumeHoverOnTargetMount = false,
  keepOpenWhileContentFocused = false,
}: PopoverStateProps): PopoverState {
  const currentDocument = useCurrentDocument();
  const { spacing4 } = useTheme();
  const [targetRef, setTargetRef] = useState<HTMLSpanElement | null>(null);
  const [contentRef, setContentRef] = useState<HTMLDivElement | null>(null);

  const overridePopoverProps = useOverridePopoverProps();
  // If the context overrides the trigger, use that instead.
  trigger = overridePopoverProps.trigger ?? trigger;

  const [isOpen, setOpen] = useState(false);
  const [availableHeight, setAvailableHeight] = useState<number>();
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current != null) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = undefined;
    }
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
    }
  }, []);

  const maxHeightModifier: Modifier<string, object> = useMemo(
    () => ({
      name: 'maxHeight',
      enabled: true,
      phase: 'main',
      requiresIfExists: ['offset', 'flip'],
      fn({ state }) {
        // In-flow popovers are clipped by ancestors; only portaled content needs a viewport height cap.
        if (!usePortal) {
          setAvailableHeight(undefined);
          return;
        }

        const overflow = detectOverflow(state, {
          elementContext: 'reference',
          padding: spacing4,
          boundary: [] as Element[],
          rootBoundary: 'viewport',
        });
        const nextAvailableHeight = getPortaledAvailableHeight(
          state.placement,
          overflow,
          state.rects?.reference?.height ?? 0
        );
        if (nextAvailableHeight == null) {
          setAvailableHeight(undefined);
          return;
        }
        setAvailableHeight(previous => (previous === nextAvailableHeight ? previous : nextAvailableHeight));
      },
    }),
    [spacing4, usePortal]
  );

  const popper = usePopper(targetRef, contentRef, {
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          padding: spacing4,
        },
      },
      maxHeightModifier,
      ...modifiers,
    ],
    placement,
    strategy,
  });
  const { styles, attributes, state, update } = popper;
  const isMobile = useIsMobile();

  // Popper doesn't observe content resize, so reposition manually to avoid drifting offscreen once data loads in.
  useEffect(() => {
    if (!contentRef || !update) {
      return;
    }
    let frame: number | undefined;
    const resizeObserver = new ResizeObserver(() => {
      // rAF leaves the ResizeObserver delivery cycle; isConnected guards a disconnected element's zeroed rect from snapping the popover to (0,0).
      frame = window.requestAnimationFrame(() => {
        frame = undefined;
        if (contentRef.isConnected) {
          update();
        }
      });
    });
    resizeObserver.observe(contentRef);
    return () => {
      resizeObserver.disconnect();
      if (frame != null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [contentRef, update]);

  const onClickTarget = () => {
    if (trigger === 'click') {
      toggle();
    }
  };

  const toggle = () => (isOpen ? close() : open());

  const close = useCallback(() => {
    onClose && onClose();
    setOpen(false);
    setAvailableHeight(undefined);
  }, [onClose]);

  const open = useCallback(() => {
    onOpen && onOpen();
    setOpen(true);
  }, [onOpen]);

  // e.target contains the element that is being leaved
  // While the popover is closed, contentRef is null. Treat that as "not leaving content" so we do
  // not cancel a pending hover-open on every mouseleave (including moves between target children).
  // This applies to every hover popover, not only resumeHoverOnTargetMount.
  const isLeavingContent = (e: React.MouseEvent) =>
    e.target === window || (contentRef != null && contentRef.contains(e.target as HTMLElement));

  // if leaving target, close unless the next element is the content
  const isLeavingPopover = (e: React.MouseEvent) => {
    if (e.relatedTarget === window || e.target === window || !targetRef) {
      return true;
    }

    const relatedTarget = e.relatedTarget as Node | null;

    // Popover content is not mounted while closed. Only cancel a pending hover-open when the
    // pointer actually leaves the target, not on every mouseleave event.
    if (contentRef == null) {
      return relatedTarget == null || !targetRef.contains(relatedTarget);
    }

    return targetRef.contains(e.target as Node) && !contentRef.contains(relatedTarget);
  };

  const isClickingOutsidePopover = useCallback(
    (e: MouseEvent | TouchEvent) => {
      return (
        !targetRef || !contentRef || (!targetRef.contains(e.target as Node) && !contentRef.contains(e.target as Node))
      );
    },
    [contentRef, targetRef]
  );

  const targetDocument = popoverDocument ?? currentDocument;

  const containsPopoverNode = useCallback(
    (node: Node | null) => node != null && (!!targetRef?.contains(node) || !!contentRef?.contains(node)),
    [contentRef, targetRef]
  );

  const hasFocusWithinContent = useCallback(() => {
    return contentRef?.contains(targetDocument.activeElement) ?? false;
  }, [contentRef, targetDocument]);

  // Mouse events only, since mobile can fire ghost mouseenter/mouseleave after a tap; focus/blur stay
  // available on mobile as the way to open/close a hover popover there (see isHoverTrigger below).
  const hoverEnabled = trigger === 'hover' && !isMobile;
  // iOS needs no listener at all (not just a no-op) to avoid requiring a second tap, so these are
  // undefined rather than early-returning.
  const isHoverTrigger = trigger === 'hover';

  const scheduleOpenOnHover = useCallback(
    (options?: { defer?: boolean }) => {
      clearCloseTimer();
      clearOpenTimer();
      const delayMs = delay ?? 0;
      // Defer from effects so setOpen is not synchronous (react-hooks/set-state-in-effect).
      if (delayMs > 0 || options?.defer) {
        openTimerRef.current = setTimeout(() => {
          open();
          openTimerRef.current = undefined;
        }, delayMs);
      } else {
        open();
      }
    },
    [clearCloseTimer, clearOpenTimer, delay, open]
  );

  const onMouseEnter = hoverEnabled
    ? (e: React.MouseEvent<HTMLSpanElement | HTMLDivElement>) => {
        if (resumeHoverOnTargetMount) {
          recordPointerPosition(e.clientX, e.clientY, e.currentTarget.ownerDocument);
        }
        scheduleOpenOnHover();
      }
    : undefined;

  const onMouseLeave = hoverEnabled
    ? (e: React.MouseEvent) => {
        const leavingContent = isLeavingContent(e);
        const leavingPopover = isLeavingPopover(e);
        if (leavingContent || leavingPopover) {
          clearOpenTimer();
          if (keepOpenWhileContentFocused && hasFocusWithinContent()) {
            clearCloseTimer();
            return;
          }
          const closeDelayMs = hideDelay ?? delay ?? 0;
          if (closeDelayMs > 0) {
            clearCloseTimer();
            closeTimerRef.current = setTimeout(() => {
              close();
              closeTimerRef.current = undefined;
            }, closeDelayMs);
          } else {
            close();
          }
        }
      }
    : undefined;

  const onFocusTarget = isHoverTrigger ? () => open() : undefined;
  // Don't close if focus is moving into the popover content (e.g. clicking a ToggleGroup inside it).
  const onBlurTarget = isHoverTrigger
    ? (e: React.FocusEvent<HTMLSpanElement>) => {
        if (!contentRef || !contentRef.contains(e.relatedTarget as Node | null)) {
          close();
        }
      }
    : undefined;
  const onFocusContent = keepOpenWhileContentFocused ? () => clearCloseTimer() : undefined;
  const onBlurContent = keepOpenWhileContentFocused
    ? (e: React.FocusEvent<HTMLDivElement>) => {
        if (!containsPopoverNode(e.relatedTarget as Node | null)) {
          close();
        }
      }
    : undefined;
  const targetPortalElement =
    popoverDocument != null ? (popoverDocument.getElementById(POPOVER_PORTAL_ID) ?? popoverDocument.body) : undefined;

  useEffect(() => {
    return () => {
      clearOpenTimer();
      clearCloseTimer();
    };
  }, [clearCloseTimer, clearOpenTimer]);

  useEffect(() => {
    if (!resumeHoverOnTargetMount || !hoverEnabled) {
      return;
    }

    return ensurePointerPositionTracking(targetDocument);
  }, [hoverEnabled, resumeHoverOnTargetMount, targetDocument]);

  // Opt-in for virtualized dropdown rows (SearchSelect): highlight changes can remount the target
  // and clear the hover-open timer without firing another mouseenter. Re-schedule open when the
  // pointer is still over the new target element.
  useEffect(() => {
    if (!resumeHoverOnTargetMount || !hoverEnabled || targetRef == null || isOpen) {
      return;
    }

    let cancelled = false;

    const tryResumeHoverOpen = () => {
      if (cancelled || targetRef == null || isOpen || openTimerRef.current != null) {
        return false;
      }

      if (!isPointerOverElement(targetRef)) {
        return false;
      }

      scheduleOpenOnHover({ defer: true });
      return true;
    };

    if (!tryResumeHoverOpen()) {
      const frameId = requestAnimationFrame(() => {
        tryResumeHoverOpen();
      });

      return () => {
        cancelled = true;
        cancelAnimationFrame(frameId);
      };
    }

    return () => {
      cancelled = true;
    };
  }, [hoverEnabled, isOpen, resumeHoverOnTargetMount, scheduleOpenOnHover, targetRef]);

  useEffect(() => {
    if (isOpen) {
      const handleClickOutside = (e: MouseEvent | TouchEvent) => {
        if (contentRef && isClickingOutsidePopover(e)) {
          if (closeOnClickOutside) {
            close();
          }

          onClickOutside && onClickOutside(e);
        }
      };
      targetDocument.addEventListener('mousedown', handleClickOutside);
      if (isMobile) {
        targetDocument.addEventListener('touchstart', handleClickOutside);
      }
      return () => {
        targetDocument.removeEventListener('mousedown', handleClickOutside);
        if (isMobile) {
          targetDocument.removeEventListener('touchstart', handleClickOutside);
        }
      };
    }
  }, [
    targetDocument,
    isMobile,
    isOpen,
    close,
    closeOnClickOutside,
    contentRef,
    isClickingOutsidePopover,
    onClickOutside,
  ]);

  return {
    onClickTarget,
    onMouseEnterTarget: onMouseEnter,
    onMouseEnterContent: onMouseEnter,
    onMouseLeave,
    onFocusTarget,
    onBlurTarget,
    onFocusContent,
    onBlurContent,
    targetRef,
    contentRef,
    setTargetRef,
    setContentRef,
    isOpen,
    close,
    open,
    toggle,
    placement,
    isSmall,
    usePortal,
    portalElement: targetPortalElement,
    styles: { popper: styles.popper },
    attributes,
    state,
    update,
    spacing: spacing4,
    availableHeight,
    preventOverflow,
    noPaddingAndBorder,
  };
}
